"use client";

import { useState, useEffect } from "react";
import type { SupplierEvaluationProject, AssessmentForm } from "../types";

interface Props {
  project: SupplierEvaluationProject;
  assessmentForm: AssessmentForm | null;
  generating: boolean;
  onGenerate: () => void;
  onSaveForm: (form: AssessmentForm) => Promise<void>;
  onEvaluate: () => void;
  evaluating: boolean;
}

export function Step2Questionnaire({ project, assessmentForm, generating, onGenerate, onSaveForm, onEvaluate, evaluating }: Props) {
  const [editableForm, setEditableForm] = useState<AssessmentForm | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);

  useEffect(() => {
    setEditableForm(assessmentForm ? JSON.parse(JSON.stringify(assessmentForm)) : null);
    setDirty(false);
  }, [assessmentForm]);

  const updateQuestion = async (
    si: number, qi: number,
    fields: Partial<{ question_text: string; guidance: string; require_evidence: boolean }>,
  ) => {
    if (!editableForm) return;
    const updated: AssessmentForm = JSON.parse(JSON.stringify(editableForm));
    Object.assign(updated.sections[si].questions[qi], fields);
    setEditableForm(updated);
    setDirty(true);
    await onSaveForm(updated);
    setDirty(false);
  };

  const addQuestion = (si: number) => {
    if (!editableForm) return;
    const updated: AssessmentForm = JSON.parse(JSON.stringify(editableForm));
    const sec = updated.sections[si];
    const newId = `${sec.section_id}Q${String(sec.questions.length + 1).padStart(2, "0")}_custom`;
    sec.questions.push({ question_id: newId, question_text: "", guidance: "" });
    setEditableForm(updated);
    setDirty(true);
  };

  const removeQuestion = async (si: number, qi: number) => {
    if (!editableForm) return;
    const updated: AssessmentForm = JSON.parse(JSON.stringify(editableForm));
    updated.sections[si].questions.splice(qi, 1);
    setEditableForm(updated);
    setDirty(true);
    await onSaveForm(updated);
    setDirty(false);
  };

  const handleSave = async () => {
    if (!editableForm) return;
    setSaving(true);
    try { await onSaveForm(editableForm); setDirty(false); }
    finally { setSaving(false); }
  };

  const totalQ = editableForm?.sections?.reduce((a, s) => a + s.questions.length, 0) ?? 0;

  return (
    <div className="diag-card" style={{ gap: "16px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 600 }}>AI-Generated Questionnaire</h3>
          <p style={{ fontSize: "13px", color: "var(--ink-mute)", marginTop: "2px" }}>
            {editableForm
              ? `${totalQ} questions across ${editableForm.sections?.length ?? 0} sections`
              : `Generate a tailored questionnaire for ${project.supplier_info.company_name}`}
          </p>
        </div>
        <button onClick={onGenerate} disabled={generating} style={primaryBtnStyle}>
          {generating ? "Generating…" : editableForm ? "⚡ Regenerate" : "⚡ Generate"}
        </button>
      </div>

      {generating && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "var(--ink-mute)", fontSize: "14px" }}>
          <div style={{ marginBottom: "12px", fontSize: "24px" }}>⚡</div>
          Generating for {project.supplier_info.company_name}…
        </div>
      )}

      {!generating && editableForm && (
        <>
          <div style={{ padding: "10px 14px", borderRadius: "8px", background: "rgba(91,108,255,0.08)", border: "1px solid rgba(91,108,255,0.2)", fontSize: "13px", color: "var(--ink-soft)" }}>
            🤖 Tailored for <strong>{project.supplier_info.company_name}</strong> · {editableForm.form_meta?.industry_vertical}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {editableForm.sections?.map((section, si) => (
              <div key={section.section_id} style={{ border: "1px solid var(--line)", borderRadius: "8px", overflow: "hidden" }}>
                <button
                  onClick={() => setOpenSection(openSection === section.section_id ? null : section.section_id)}
                  style={{ width: "100%", textAlign: "left", padding: "12px 16px", background: "var(--paper-3)", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--ink)" }}>{section.section_title}</span>
                  <span style={{ fontSize: "12px", color: "var(--ink-mute)" }}>{section.questions.length} questions</span>
                </button>

                {openSection === section.section_id && (
                  <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                    {section.questions.map((q, qi) => (
                      <div key={q.question_id} style={{ borderLeft: "3px solid var(--line-strong)", paddingLeft: "12px" }}>
                        <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                          <span style={{ fontSize: "12px", color: "var(--ink-mute)", marginTop: "2px", flexShrink: 0 }}>Q{qi + 1}.</span>
                          <input
                            style={{ flex: 1, background: "var(--paper-3)", border: "1px solid var(--line)", borderRadius: "6px", padding: "5px 8px", fontSize: "13px", color: "var(--ink)" }}
                            value={q.question_text}
                            onChange={(e) => updateQuestion(si, qi, { question_text: e.target.value })}
                          />
                          <button onClick={() => removeQuestion(si, qi)} style={{ background: "none", border: "none", color: "var(--ink-mute)", cursor: "pointer", padding: "4px", fontSize: "16px" }}>×</button>
                        </div>
                        {q.guidance && (
                          <p style={{ fontSize: "12px", color: "var(--ink-mute)", marginTop: "4px", marginLeft: "24px" }}>
                            Guidance: {q.guidance}
                          </p>
                        )}
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px", marginLeft: "24px" }}>
                          <input
                            type="checkbox"
                            id={`req-${q.question_id}`}
                            checked={!!q.require_evidence}
                            onChange={(e) => updateQuestion(si, qi, { require_evidence: e.target.checked })}
                          />
                          <label htmlFor={`req-${q.question_id}`} style={{ fontSize: "12px", color: "var(--ink-mute)", cursor: "pointer" }}>
                            Require Evidence
                          </label>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addQuestion(si)} style={{ ...ghostBtnStyle, alignSelf: "flex-start", fontSize: "12px" }}>
                      + Add question
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: "12px", borderTop: "1px solid var(--line)" }}>
            <button onClick={handleSave} disabled={saving || !dirty} style={ghostBtnStyle}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
            <button onClick={onEvaluate} disabled={evaluating || dirty} style={primaryBtnStyle}>
              {evaluating ? "…" : "Next – Invite Supplier →"}
            </button>
          </div>
          {dirty && (
            <p style={{ fontSize: "12px", color: "#F59E0B", textAlign: "right", marginTop: "-8px" }}>
              Unsaved changes — save before sending.
            </p>
          )}
        </>
      )}

      {!generating && !editableForm && (
        <div style={{ textAlign: "center", padding: "40px 0", border: "2px dashed var(--line-strong)", borderRadius: "8px", color: "var(--ink-mute)", fontSize: "14px" }}>
          Click Generate to create the questionnaire
        </div>
      )}
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  background: "linear-gradient(135deg, var(--indigo), var(--magenta))",
  border: "none", borderRadius: "8px", padding: "8px 16px",
  fontSize: "13px", fontWeight: 600, color: "#fff", cursor: "pointer",
};
const ghostBtnStyle: React.CSSProperties = {
  background: "var(--paper-2)", border: "1px solid var(--line-strong)",
  borderRadius: "8px", padding: "7px 14px", fontSize: "13px",
  color: "var(--ink-soft)", cursor: "pointer",
};
